/**
 * iter126 — MFE / MAE Card
 *
 * Self-fetching card that consumes GET /api/trades/{id}/mfe-mae and
 * renders one of three states:
 *
 *  ● AVAILABLE (mfe_source === "binance_klines"):
 *      Show MFE + MAE numbers, a "capture ratio" insight for winners
 *      ("You captured X% of the max favorable move — left Y% on the
 *      table"), or a "trade never really worked" note for losers
 *      whose MAE far exceeded their realized loss.
 *
 *  ● UNAVAILABLE (mfe_source === "unavailable"):
 *      Honest plain-text explanation — mirrors the confidence-system
 *      approach of never hiding a gap. Non-crypto trades stay
 *      informed rather than silently omitted.
 *
 *  ● LOADING:
 *      Skeleton shimmer.
 *
 * Fetch policy: on-mount only. The backend caches the enrichment
 * to the trade doc so repeat mounts of the same tradeId hit
 * cache_hit=true immediately.
 */
import React from "react";
import { TrendingUp, TrendingDown, Info, Loader2 } from "lucide-react";
import api from "../lib/api";

// ── Copy: capture-ratio insight for winners ─────────────────────────
// The heuristic answers "how much of the max move did the trader
// actually capture?" — a coachable metric that surfaces early-exit
// patterns without shaming the user.
function insightForWinner(realized, mfe) {
  if (mfe <= 0.0001) {
    return {
      tone: "info",
      headline: "Straight-line winner",
      detail: "The price barely moved against your entry — you rode a clean trend.",
    };
  }
  const captureRatio = Math.max(0, Math.min(1, realized / mfe));
  const captured = Math.round(captureRatio * 100);
  const left = 100 - captured;
  if (captured >= 85) {
    return {
      tone: "positive",
      headline: `Near-perfect capture — ${captured}% of the max move`,
      detail: "You held close to the peak of this trade's favorable run. Well timed.",
    };
  }
  if (captured >= 40) {
    return {
      tone: "info",
      headline: `You captured ${captured}% of the max favorable move`,
      detail: `Left about ${left}% of the move on the table. Worth reviewing whether the exit was intentional or reactive.`,
    };
  }
  return {
    tone: "warning",
    headline: `Early exit — captured only ${captured}% of the max move`,
    detail: `The trade ran +${mfe.toFixed(2)}% at its peak, but you exited at +${realized.toFixed(2)}%. Left ${left}% on the table.`,
  };
}

// ── Copy: how deep did a loser dig before it stopped? ────────────────
function insightForLoser(realizedNeg, maePct) {
  // realizedNeg is negative here; convert to abs magnitude for compare.
  const realizedMag = Math.abs(realizedNeg);
  if (maePct <= realizedMag * 1.15) {
    // MAE within 15% of realized — trade closed near its floor
    return {
      tone: "info",
      headline: "Trade closed close to its worst point",
      detail: "The exit was near the deepest drawdown of the hold. Not a lot of room this one worked.",
    };
  }
  // MAE meaningfully worse than the realized loss → floated red for a while
  const ratio = maePct / Math.max(realizedMag, 0.01);
  return {
    tone: "warning",
    headline: "This trade never really worked",
    detail: `Peak drawdown ${maePct.toFixed(2)}% vs realized ${realizedNeg.toFixed(2)}% — the position was ${ratio.toFixed(1)}× underwater at its worst before you exited.`,
  };
}

// ── Copy: winners that never dipped much ────────────────────────────
function insightForBigMaeWinner(realized, maePct) {
  return {
    tone: "info",
    headline: "Held through pain",
    detail: `The trade dipped ${maePct.toFixed(2)}% underwater at its worst before recovering to +${realized.toFixed(2)}%. Discipline paid off, but consider whether the entry had a wider stop than you'd normally accept.`,
  };
}

const TONE_STYLES = {
  positive: {
    fg: "var(--bt-accent)",
    bg: "color-mix(in srgb, var(--bt-accent) 10%, var(--bt-surface))",
    border: "color-mix(in srgb, var(--bt-accent) 40%, var(--bt-border))",
    Icon: TrendingUp,
  },
  info: {
    fg: "var(--bt-text)",
    bg: "var(--bt-surface)",
    border: "var(--bt-border)",
    Icon: Info,
  },
  warning: {
    fg: "#ff9500",
    bg: "color-mix(in srgb, #ff9500 8%, var(--bt-surface))",
    border: "color-mix(in srgb, #ff9500 40%, var(--bt-border))",
    Icon: TrendingDown,
  },
};

function Stat({ label, value, testid, color }) {
  return (
    <div className="flex-1 min-w-[110px]">
      <div className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">{label}</div>
      <div data-testid={testid} className="font-mono text-base font-semibold mt-0.5" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export function MfeMaeCard({ tradeId, testid = "mfe-mae-card" }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    if (!tradeId) return;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const { data } = await api.get(`/trades/${tradeId}/mfe-mae`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({
          loading: false, data: null,
          error: e?.response?.data?.detail || "Couldn't load MFE/MAE right now.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [tradeId]);

  // ── Loading ──
  if (state.loading) {
    return (
      <div
        data-testid={`${testid}-loading`}
        className="rounded-sm border p-3 flex items-center gap-2"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)" }}
      >
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--bt-text-muted)" }} />
        <span className="text-xs text-[var(--bt-text-muted)]">Fetching max-excursion data…</span>
      </div>
    );
  }

  // ── Error ──
  if (state.error) {
    return (
      <div
        data-testid={`${testid}-error`}
        className="rounded-sm border p-3 text-xs"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)", color: "var(--bt-text-muted)" }}
      >
        {typeof state.error === "string" ? state.error : "Couldn't load MFE/MAE."}
      </div>
    );
  }

  const d = state.data;

  // ── Unavailable ──
  if (d?.mfe_source === "unavailable") {
    return (
      <div
        data-testid={`${testid}-unavailable`}
        className="rounded-sm border p-3"
        style={{ borderColor: "var(--bt-border)", background: "var(--bt-surface)" }}
      >
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--bt-text-muted)" }} />
          <div className="flex-1">
            <div className="text-xs font-medium text-[var(--bt-text)]">
              Price-history unavailable for this broker
            </div>
            <div className="text-[11px] text-[var(--bt-text-muted)] mt-1 leading-relaxed">
              {d.reason || "MFE/MAE requires a live price feed. Available only for Delta / crypto trades where we can map the symbol to Binance's public candles."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Available: compute the right insight ──────────────────────────
  const realized = d.realized_pnl_pct ?? 0;
  const mfePct = d.mfe_pct ?? 0;
  const maePct = d.mae_pct ?? 0;

  let insight;
  if (realized >= 0) {
    // Winner
    if (maePct >= 1.5 * Math.max(realized, 0.5)) {
      // Held through significant drawdown before winning
      insight = insightForBigMaeWinner(realized, maePct);
    } else {
      insight = insightForWinner(realized, mfePct);
    }
  } else {
    insight = insightForLoser(realized, maePct);
  }
  const tone = TONE_STYLES[insight.tone] || TONE_STYLES.info;
  const { Icon } = tone;

  return (
    <div
      data-testid={testid}
      className="rounded-sm border p-3 space-y-3"
      style={{ borderColor: tone.border, background: tone.bg }}
    >
      {/* Header: insight headline + icon */}
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: tone.fg }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">
            Max-excursion analysis
          </div>
          <div className="text-sm font-semibold mt-0.5" style={{ color: tone.fg }}>
            {insight.headline}
          </div>
          <div className="text-[11px] text-[var(--bt-text-muted)] mt-1 leading-relaxed">
            {insight.detail}
          </div>
        </div>
      </div>

      {/* Stats row: realized / MFE / MAE */}
      <div className="flex flex-wrap gap-3 pt-2 border-t" style={{ borderColor: tone.border }}>
        <Stat
          label="Realized"
          testid={`${testid}-realized`}
          value={`${realized >= 0 ? "+" : ""}${realized.toFixed(2)}%`}
          color={realized >= 0 ? "var(--bt-accent)" : "var(--bt-danger)"}
        />
        <Stat
          label="Max favorable"
          testid={`${testid}-mfe`}
          value={`+${mfePct.toFixed(2)}%`}
          color="var(--bt-accent)"
        />
        <Stat
          label="Max adverse"
          testid={`${testid}-mae`}
          value={`-${maePct.toFixed(2)}%`}
          color="var(--bt-danger)"
        />
      </div>

      {/* Footer: source attribution — trader trust matters */}
      <div className="text-[10px] text-[var(--bt-text-muted)] pt-1 flex items-center justify-between gap-2">
        <span>
          {d.candles_scanned} × {d.interval} candles · {d.binance_symbol}
        </span>
        {d.cache_hit && (
          <span title="Served from local cache — no fresh Binance fetch"
                className="opacity-60">cached</span>
        )}
      </div>
    </div>
  );
}

export default MfeMaeCard;
